import { IEventAggregationService } from '@studyportals/event-aggregation-service-interface';
import { ITokenBasedSession, IUser, SessionDestroyedEvent } from '../../../../interfaces';
import { CatchReportAsyncException, CatchReportException } from '../../decorators/error-decorator';
import { ServerTime } from '../../infrastructure/server-time/server-time';
import { IServerTime } from '../../infrastructure/server-time/server-time.interface';
import { RefreshTokenError } from '../errors/refresh-token-error';
import { SessionRefreshedEvent } from '../events/session-refreshed-event';
import { ITokenService } from '../interfaces/token-service.interface';
import { Credentials } from './credentials';
import { CredentialsPayload } from './credentials-payload';
import { Student } from './student';

export class TokenBasedSession implements ITokenBasedSession {
	private user: IUser = null;
	private credentials: Credentials = null;
	private tokenService: ITokenService;
	private eventAggregationService: IEventAggregationService;
	private serverTime: IServerTime = new ServerTime();
	private isUseablePromise: Promise<boolean> = null;
	private accessTokenPromise: Promise<string> = null;

	constructor(tokenService: ITokenService, eventAggregationService: IEventAggregationService) {
		this.tokenService = tokenService;
		this.eventAggregationService = eventAggregationService;
	}

	@CatchReportAsyncException
	public async getAccessToken(): Promise<string> {
		if (this.accessTokenPromise !== null) {
			return this.accessTokenPromise;
		}

		try {
			this.accessTokenPromise = this.getExistingOrRefreshedAccessToken();
			return await this.accessTokenPromise;
		} finally {
			this.accessTokenPromise = null;
		}
	}

	private async getExistingOrRefreshedAccessToken(): Promise<string> {
		// No need to refresh
		if (!(await this.shouldRefresh())) {
			return this.credentials.accessToken;
		}

		// Wait for the refresh to finish
		const credentials = await this.refreshAndRetrieveCredentials();
		return credentials.accessToken;
	}

	@CatchReportAsyncException
	public async getRefreshToken(): Promise<string> {
		return this.credentials.refreshToken;
	}

	@CatchReportAsyncException
	public async destroy(): Promise<void> {
		try {
			const token = await this.getAccessToken();
			await this.tokenService.logoutSession(token);
		} finally {
			this.eventAggregationService.publishTo(SessionDestroyedEvent.EventType, new SessionDestroyedEvent());
		}
	}

	@CatchReportException
	public getUser(): IUser {
		return this.user;
	}

	@CatchReportAsyncException
	public async getCredentials(): Promise<Credentials> {
		return this.credentials;
	}

	@CatchReportAsyncException
	public setCredentials(credentials: Credentials): void {
		if (!credentials.refreshToken) {
			this.eventAggregationService.publishTo(SessionDestroyedEvent.EventType, new SessionDestroyedEvent());
			throw new RefreshTokenError('Not a valid refresh token.');
		}
		if (credentials.accessToken !== null) {
			const tokenPayload = credentials.getPayload();
			this.setUser(tokenPayload);
		}

		this.credentials = credentials;
	}

	@CatchReportAsyncException
	public setUser(credentialsPayload: CredentialsPayload): void {
		this.user = new Student(credentialsPayload.sub, credentialsPayload.email);
	}

	@CatchReportAsyncException
	public isUsable(): Promise<boolean> {
		if (this.isUseablePromise !== null) {
			return this.isUseablePromise;
		}

		this.isUseablePromise = new Promise(async (resolve) => {
			if (!(await this.shouldRefresh())) {
				return resolve(true);
			}

			try {
				await this.getAccessToken();
			} catch {
				resolve(false);
			}

			resolve(true);
		});

		return this.isUseablePromise;
	}

	public async refreshCredentials(): Promise<void> {
		await this.refreshAndRetrieveCredentials();
	}

	private async refreshAndRetrieveCredentials(): Promise<Credentials> {
		try {
			// Get new credentials
			const newCredentials = await this.tokenService.refreshCredentials(this.credentials.refreshToken);
			// Update self
			this.setCredentials(newCredentials);
			// Notify for new credentials
			this.eventAggregationService.publishTo(SessionRefreshedEvent.EventType, new SessionRefreshedEvent());
			// Return the access token
			return this.credentials;
		} catch (error) {
			if (error instanceof RefreshTokenError) {
				this.eventAggregationService.publishTo(SessionDestroyedEvent.EventType, new SessionDestroyedEvent());
			}

			throw error;
		}
	}

	private async shouldRefresh(): Promise<boolean> {
		return this.credentials === null || this.credentials.isExpired();
	}
}
